import React, { useState, useCallback, useEffect, useMemo, useRef, forwardRef, useImperativeHandle } from 'react';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { LogtSearchCriteria, FilterCondition, MessageTypeFilter } from '../../models/logTSearchCriteria';
import { fetchAllowedMessageTypes } from '../../services/allowedMessageTypesService';
import { useBetaTheme } from '../../context/BetaThemeContext';
import { formatDatePickerZuluDateTimeForApi, formatDatePickerZuluDateTimeLabel, formatZuluDateTimeLabel } from '../../utils/zuluTime';

export interface ActiveFilters {
  tailNumbers: string[];
  flightNumbers: string[];
  messageTypes: MessageTypeFilter[];
  keyword: string;
  smi: string;
  orig: string;
  dest: string;
}

export interface BetaFilterBarHandle {
  removeFilter: (type: 'tail' | 'flight' | 'msgType' | 'keyword' | 'smi' | 'orig' | 'dest', value?: string) => void;
  reset: () => void;
}

interface BetaFilterBarProps {
  onSearch: (criteria: LogtSearchCriteria) => void;
  onReset?: () => void;
  onFiltersChange?: (filters: ActiveFilters) => void;
  isLoading: boolean;
  airline: string;
  aircraftList: string[];
  accessToken: string;
  loadedMessageTypes?: string[];
}

type DateMode = 'relative' | 'specific';
type TimeUnit = 'min' | 'hours' | 'days';

const getRelativeMilliseconds = (value: number, unit: TimeUnit): number => {
  switch (unit) {
    case 'hours': return value * 3600000;
    case 'days': return value * 86400000;
    default: return value * 60000;
  }
};

const isValueOptionalMessageTypeCondition = (condition: FilterCondition): boolean => {
  return condition === 'is_empty' || condition === 'is_not_empty';
};

const BetaFilterBar = forwardRef<BetaFilterBarHandle, BetaFilterBarProps>(({ onSearch, onReset, onFiltersChange, isLoading, airline, aircraftList, accessToken, loadedMessageTypes }, ref) => {
  const { theme } = useBetaTheme();
  const [dateMode, setDateMode] = useState<DateMode>('relative');
  const [relativeValue, setRelativeValue] = useState<number>(30);
  const [relativeUnit, setRelativeUnit] = useState<TimeUnit>('min');
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(null);
  const [searchError, setSearchError] = useState(false);

  const [selectedTailNumbers, setSelectedTailNumbers] = useState<string[]>([]);
  const [tailNumberInput, setTailNumberInput] = useState('');
  const [selectedFlightNumbers, setSelectedFlightNumbers] = useState<string[]>([]);
  const [flightNumberInput, setFlightNumberInput] = useState('');
  const [selectedMessageTypes, setSelectedMessageTypes] = useState<MessageTypeFilter[]>([]);
  const [messageTypeInput, setMessageTypeInput] = useState('');
  const [messageTypeCondition, setMessageTypeCondition] = useState<FilterCondition>('contains');
  const [smi, setSmi] = useState('');
  const [orig, setOrig] = useState('');
  const [dest, setDest] = useState('');
  const [keywordSearch, setKeywordSearch] = useState('');
  const [showAircraftList, setShowAircraftList] = useState(false);
  const [showMsgTypeList, setShowMsgTypeList] = useState(false);
  const [msgTypeOptions, setMsgTypeOptions] = useState<string[]>([]);
  const [apiMsgTypes, setApiMsgTypes] = useState<string[]>([]);
  const autocompleteRef = useRef<HTMLDivElement>(null);
  const msgTypeRef = useRef<HTMLDivElement>(null);
  const shouldAutoSearch = useRef(false);

  const upsertMessageTypeFilter = (filters: MessageTypeFilter[], value: string, condition: FilterCondition): MessageTypeFilter[] => {
    const normalized = value.trim();
    if (!normalized) return filters;

    const existingIndex = filters.findIndex(m => m.Value === normalized);
    if (existingIndex >= 0) {
      const updated = [...filters];
      updated[existingIndex] = { Value: normalized, Condition: condition };
      return updated;
    }

    return [...filters, { Value: normalized, Condition: condition }];
  };

  const now = new Date();

  // Load message types from API
  useEffect(() => {
    if (!accessToken) return;

    let isMounted = true;

    const loadMessageTypes = async () => {
      try {
        const types = await fetchAllowedMessageTypes(undefined, accessToken);
        if (isMounted) {
          const list = Array.isArray(types) ? types : [];
          setApiMsgTypes(list);
          setMsgTypeOptions(list);
        }
      } catch {
        if (isMounted) {
          setMsgTypeOptions([]);
        }
      }
    };

    void loadMessageTypes();

    return () => {
      isMounted = false;
    };
  }, [accessToken]);

  // Merge loaded data message types with the API list
  useEffect(() => {
    if (loadedMessageTypes && loadedMessageTypes.length > 0) {
      const merged = Array.from(new Set([...apiMsgTypes, ...loadedMessageTypes]))
        .sort((left, right) => left.localeCompare(right));
      setMsgTypeOptions(merged);
    }
  }, [loadedMessageTypes, apiMsgTypes]);

  // Close autocomplete on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (autocompleteRef.current && !autocompleteRef.current.contains(e.target as Node)) {
        setShowAircraftList(false);
      }
      if (msgTypeRef.current && !msgTypeRef.current.contains(e.target as Node)) {
        setShowMsgTypeList(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // Notify parent of active filter selections in real-time
  useEffect(() => {
    if (onFiltersChange) {
      const tailNums = [...selectedTailNumbers];
      if (tailNumberInput.trim() && !tailNums.includes(tailNumberInput.trim())) {
        tailNums.push(tailNumberInput.trim());
      }
      const flightNums = [...selectedFlightNumbers];
      if (flightNumberInput.trim() && !flightNums.includes(flightNumberInput.trim())) {
        flightNums.push(flightNumberInput.trim());
      }
      let msgTypes = [...selectedMessageTypes];
      if (messageTypeInput.trim()) {
        msgTypes = upsertMessageTypeFilter(msgTypes, messageTypeInput, messageTypeCondition);
      }
      onFiltersChange({
        tailNumbers: tailNums,
        flightNumbers: flightNums,
        messageTypes: msgTypes,
        keyword: keywordSearch,
        smi,
        orig,
        dest,
      });
    }
  }, [selectedTailNumbers, selectedFlightNumbers, selectedMessageTypes, tailNumberInput, flightNumberInput, messageTypeInput, keywordSearch, smi, orig, dest, messageTypeCondition, onFiltersChange]);

  const filteredAircraft = useMemo(() => {
    const available = (aircraftList || []).filter(ac => !selectedTailNumbers.includes(ac));
    if (!tailNumberInput) return available;
    return available.filter(ac => ac.toUpperCase().includes(tailNumberInput.toUpperCase()));
  }, [aircraftList, tailNumberInput, selectedTailNumbers]);

  const zuluRelativeRange = useMemo(() => {
    const end = new Date();
    const value = relativeValue > 0 ? relativeValue : 30;
    const start = new Date(end.getTime() - getRelativeMilliseconds(value, relativeUnit));

    return {
      from: formatZuluDateTimeLabel(start),
      to: formatZuluDateTimeLabel(end),
    };
  }, [relativeValue, relativeUnit]);

  const handleSelectAircraft = (ac: string) => {
    if (!selectedTailNumbers.includes(ac)) {
      setSelectedTailNumbers(prev => [...prev, ac]);
    }
    setTailNumberInput('');
    setShowAircraftList(false);
    setSearchError(false);
  };

  const handleAddFlight = () => {
    const val = flightNumberInput.trim();
    if (val && !selectedFlightNumbers.includes(val)) {
      setSelectedFlightNumbers(prev => [...prev, val]);
    }
    setFlightNumberInput('');
    setSearchError(false);
  };

  const handleFlightKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAddFlight();
    }
  };

  const buildCriteriaWith = useCallback((msgTypes: MessageTypeFilter[]): LogtSearchCriteria => {
    let start: Date | string;
    let end: Date | string;
    const now = new Date();

    if (dateMode === 'relative' && relativeValue > 0) {
      const ms = getRelativeMilliseconds(relativeValue, relativeUnit);
      start = new Date(now.getTime() - ms);
      end = now;
    } else if (dateMode === 'specific' && startDate && endDate) {
      start = formatDatePickerZuluDateTimeForApi(startDate);
      end = formatDatePickerZuluDateTimeForApi(endDate);
    } else {
      start = new Date(now.getTime() - 30 * 60000);
      end = now;
    }

    return {
      Airline: airline || '',
      TailNumber: selectedTailNumbers.length === 1 ? selectedTailNumbers[0] : '',
      TailNumbers: selectedTailNumbers.length > 0 ? selectedTailNumbers : undefined,
      FlightNumber: selectedFlightNumbers.length === 1 ? selectedFlightNumbers[0] : '',
      FlightNumbers: selectedFlightNumbers.length > 0 ? selectedFlightNumbers : undefined,
      KeywordSearch: keywordSearch || '',
      MessageType: msgTypes.length === 1 && msgTypes[0].Condition === 'equals' ? msgTypes[0].Value : '',
      MessageTypeFilters: msgTypes.length > 0 ? msgTypes : undefined,
      SMI: smi || '',
      Source: '',
      IsXml: 0,
      StartDate: start,
      EndDate: end,
      SortColumn: 'logtTS',
      SortDirection: 'DESC',
      Page: 1,
      Limit: 25,
      Orig: orig || '',
      Dest: dest || '',
    };
  }, [dateMode, relativeValue, relativeUnit, startDate, endDate, airline, selectedTailNumbers, selectedFlightNumbers, smi, orig, dest, keywordSearch]);

  const buildCriteria = useCallback((): LogtSearchCriteria => {
    return buildCriteriaWith(selectedMessageTypes);
  }, [buildCriteriaWith, selectedMessageTypes]);

  const handleSearch = () => {
    const typedFlight = flightNumberInput.trim();
    const flightFilters = typedFlight && !selectedFlightNumbers.includes(typedFlight)
      ? [...selectedFlightNumbers, typedFlight]
      : selectedFlightNumbers;

    if (selectedTailNumbers.length === 0 && flightFilters.length === 0) {
      setSearchError(true);
      return;
    }
    setSearchError(false);

    if (typedFlight && !selectedFlightNumbers.includes(typedFlight)) {
      setSelectedFlightNumbers(flightFilters);
      setFlightNumberInput('');
    }

    // Auto-add typed message type text if user hasn't selected from dropdown
    let msgTypes = [...selectedMessageTypes];
    if (messageTypeInput.trim()) {
      msgTypes = upsertMessageTypeFilter(msgTypes, messageTypeInput, messageTypeCondition);
      setSelectedMessageTypes(msgTypes);
      setMessageTypeInput('');
    } else if (isValueOptionalMessageTypeCondition(messageTypeCondition)
      && !msgTypes.some(m => m.Condition === messageTypeCondition && !m.Value?.trim())) {
      msgTypes = [...msgTypes, { Value: '', Condition: messageTypeCondition }];
      setSelectedMessageTypes(msgTypes);
    }

    const criteria = buildCriteriaWith(msgTypes);
    criteria.FlightNumber = flightFilters.length === 1 ? flightFilters[0] : '';
    criteria.FlightNumbers = flightFilters.length > 0 ? flightFilters : undefined;

    onSearch(criteria);
  };

  const handleReset = () => {
    setDateMode('relative');
    setRelativeValue(30);
    setRelativeUnit('min');
    setStartDate(null);
    setEndDate(null);
    setSelectedTailNumbers([]);
    setTailNumberInput('');
    setSelectedFlightNumbers([]);
    setFlightNumberInput('');
    setSelectedMessageTypes([]);
    setMessageTypeInput('');
    setSmi('');
    setOrig('');
    setDest('');
    setKeywordSearch('');
    setSearchError(false);
    setMessageTypeCondition('contains');
    setShowAircraftList(false);
    setShowMsgTypeList(false);
    onReset?.();
  };

  const filteredMsgTypes = useMemo(() => {
    const selectedValues = new Set(selectedMessageTypes.map(m => m.Value));
    const available = (msgTypeOptions || []).filter(t => !selectedValues.has(t));
    if (!messageTypeInput) return available;
    return available.filter(t => t.toLowerCase().includes(messageTypeInput.toLowerCase()));
  }, [msgTypeOptions, messageTypeInput, selectedMessageTypes]);

  const handleSelectMsgType = (t: string) => {
    setSelectedMessageTypes(prev => upsertMessageTypeFilter(prev, t, messageTypeCondition));
    setMessageTypeInput('');
    setShowMsgTypeList(false);
  };

  // Expose imperative handle for removing individual filters from parent
  useImperativeHandle(ref, () => ({
    reset() {
      handleReset();
    },
    removeFilter(type, value) {
      shouldAutoSearch.current = true;
      switch (type) {
        case 'tail':
          if (tailNumberInput.trim() === value) {
            setTailNumberInput('');
          }
          setSelectedTailNumbers(prev => prev.filter(t => t !== value));
          break;
        case 'flight':
          if (flightNumberInput.trim() === value) {
            setFlightNumberInput('');
          }
          setSelectedFlightNumbers(prev => prev.filter(f => f !== value));
          break;
        case 'msgType':
          if ((messageTypeInput || '').trim() === (value || '').trim()) {
            setMessageTypeInput('');
          }
          setSelectedMessageTypes(prev => prev.filter(m => m.Value !== value));
          break;
        case 'keyword':
          setKeywordSearch('');
          break;
        case 'smi':
          setSmi('');
          break;
        case 'orig':
          setOrig('');
          break;
        case 'dest':
          setDest('');
          break;
      }
    },
  }));

  // Auto-trigger search when a filter is removed via badge close button
  useEffect(() => {
    if (shouldAutoSearch.current) {
      shouldAutoSearch.current = false;
      if (selectedTailNumbers.length > 0 || selectedFlightNumbers.length > 0) {
        onSearch(buildCriteria());
      } else {
        onReset?.();
      }
    }
  }, [selectedTailNumbers, selectedFlightNumbers, selectedMessageTypes, keywordSearch, smi, orig, dest]);

  return (
    <form className="beta-filter-sidebar" onSubmit={(e) => { e.preventDefault(); handleSearch(); }}>
      <div className="beta-filter-sidebar-header">
        <span className="beta-filter-sidebar-title">FILTERS</span>
      </div>

      <div className="beta-filter-sidebar-body">
        {/* Aircraft # - Multi-select */}
        <div className="beta-filter-field beta-filter-autocomplete" ref={autocompleteRef}>
          <span className="beta-filter-label" style={{ color: searchError ? 'red' : undefined }}>Aircraft #</span>
          <div className="beta-filter-combo-wrapper">
            <input type="text" className="beta-filter-input" value={tailNumberInput}
              style={{ borderColor: searchError && selectedTailNumbers.length === 0 ? 'red' : undefined }}
              onChange={(e) => { setTailNumberInput(e.target.value); setShowAircraftList(true); setSearchError(false); }}
              onFocus={() => setShowAircraftList(true)} placeholder={selectedTailNumbers.length > 0 ? 'Add more...' : 'e.g. N123AB'} />
            <button type="button" className="beta-combo-arrow" aria-label="Open list"
              onClick={() => setShowAircraftList(prev => !prev)}>▾</button>
          </div>
          {showAircraftList && filteredAircraft.length > 0 && (
            <ul className="beta-filter-autocomplete-list">
              {filteredAircraft.slice(0, 20).map((ac) => (
                <li key={ac} className="beta-filter-autocomplete-item" tabIndex={0}
                  onClick={() => handleSelectAircraft(ac)}
                  onKeyDown={(e) => { if (e.key === 'Enter') handleSelectAircraft(ac); }}>
                  {ac}
                </li>
              ))}
            </ul>
          )}
          {searchError && selectedTailNumbers.length === 0 && (
            <div className="beta-filter-error">Aircraft # or Flight # required</div>
          )}
        </div>

        {/* Flight # - Multi-select */}
        <div className="beta-filter-field">
          <span className="beta-filter-label" style={{ color: searchError ? 'red' : undefined }}>Flight #</span>
          <div className="beta-filter-combo-wrapper">
            <input type="text" className="beta-filter-input" value={flightNumberInput}
              style={{ borderColor: searchError && selectedFlightNumbers.length === 0 ? 'red' : undefined }}
              onChange={(e) => { const v = e.target.value.replace(/[^A-Z0-9]/gi, '').toUpperCase(); setFlightNumberInput(v); setSearchError(false); }}
              onKeyDown={handleFlightKeyDown}
              placeholder={selectedFlightNumbers.length > 0 ? 'Add more...' : 'e.g. 1234'} />
            <button type="button" className="beta-combo-arrow" aria-label="Add flight"
              onClick={handleAddFlight}>+</button>
          </div>
          {searchError && selectedFlightNumbers.length === 0 && (
            <div className="beta-filter-error">Aircraft # or Flight # required</div>
          )}
        </div>

        {/* Message Type - Multi-select with per-filter condition */}
        <div className="beta-filter-field beta-filter-autocomplete" ref={msgTypeRef}>
          <span className="beta-filter-label">Msg Type</span>
          <div className="beta-filter-msgtype-row">
            <select className="beta-filter-condition-select" value={messageTypeCondition} onChange={(e) => setMessageTypeCondition(e.target.value as FilterCondition)}>
              <option value="contains">Contains</option>
              <option value="equals">Equals</option>
              <option value="equals_not">Not equal</option>
              <option value="contains_not">Not contains</option>
              <option value="starts_with">Starts with</option>
              <option value="ends_with">Ends with</option>
              <option value="is_empty">Is empty</option>
              <option value="is_not_empty">Is not empty</option>
            </select>
            <div className="beta-filter-combo-wrapper">
              <input type="text" className="beta-filter-input" value={messageTypeInput}
                onChange={(e) => { setMessageTypeInput(e.target.value); setShowMsgTypeList(true); }}
                onFocus={() => setShowMsgTypeList(true)}
                placeholder={selectedMessageTypes.length > 0 ? 'Add more...' : 'e.g. OOOI'} />
              <button type="button" className="beta-combo-arrow" aria-label="Open list"
                onClick={() => setShowMsgTypeList(prev => !prev)}>▾</button>
            </div>
          </div>
          {showMsgTypeList && filteredMsgTypes.length > 0 && (
            <ul className="beta-filter-autocomplete-list">
              {filteredMsgTypes.slice(0, 25).map((t) => (
                <li key={t} className="beta-filter-autocomplete-item" tabIndex={0}
                  onClick={() => handleSelectMsgType(t)}
                  onKeyDown={(e) => { if (e.key === 'Enter') handleSelectMsgType(t); }}>
                  {t}
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* Keyword */}
        <div className="beta-filter-field">
          <span className="beta-filter-label">Keyword</span>
          <input type="text" className="beta-filter-input" value={keywordSearch}
            onChange={(e) => setKeywordSearch(e.target.value)} placeholder="Search text..."
            onKeyDown={(e) => { if (e.key === 'Enter') e.preventDefault(); }} />
        </div>

        {/* SMI */}
        <div className="beta-filter-field">
          <span className="beta-filter-label">SMI</span>
          <input type="text" className="beta-filter-input" value={smi}
            onChange={(e) => setSmi(e.target.value.toUpperCase())}
            onKeyDown={(e) => { if (e.key === 'Enter') e.preventDefault(); }} />
        </div>

        {/* Orig / Dest row */}
        <div className="beta-filter-field-row">
          <div className="beta-filter-field beta-filter-field--half">
            <span className="beta-filter-label">Orig</span>
            <input type="text" className="beta-filter-input" value={orig}
              onChange={(e) => setOrig(e.target.value.toUpperCase())}
              onKeyDown={(e) => { if (e.key === 'Enter') e.preventDefault(); }} />
          </div>
          <div className="beta-filter-field beta-filter-field--half">
            <span className="beta-filter-label">Dest</span>
            <input type="text" className="beta-filter-input" value={dest}
              onChange={(e) => setDest(e.target.value.toUpperCase())}
              onKeyDown={(e) => { if (e.key === 'Enter') e.preventDefault(); }} />
          </div>
        </div>

        {/* Date Range */}
        <div className="beta-filter-field">
          <span className="beta-filter-label">Date Range</span>
          <div className="beta-filter-radio-group">
            <label className="beta-filter-radio-label">
              <input type="radio" name="betaDateMode" checked={dateMode === 'relative'} onChange={() => setDateMode('relative')} />
              <span>Current</span>
            </label>
            <label className="beta-filter-radio-label">
              <input type="radio" name="betaDateMode" checked={dateMode === 'specific'} onChange={() => setDateMode('specific')} />
              <span>Historical</span>
            </label>
          </div>
        </div>

        {dateMode === 'relative' ? (
          <>
            <div className="beta-filter-field-row">
              <div className="beta-filter-field beta-filter-field--half">
                <span className="beta-filter-label">Last</span>
                <input type="number" className="beta-filter-input" value={relativeValue === 0 ? '' : relativeValue}
                  onChange={(e) => {
                    const val = e.target.value;
                    if (val === '') {
                      setRelativeValue(0);
                    } else {
                      const num = Number.parseInt(val, 10);
                      if (!Number.isNaN(num)) {
                        setRelativeValue(Math.min(Math.max(num, 0), 999));
                      }
                    }
                  }} min={1} />
              </div>
              <div className="beta-filter-field beta-filter-field--half">
                <span className="beta-filter-label">Unit</span>
                <select className="beta-filter-select" value={relativeUnit} onChange={(e) => setRelativeUnit(e.target.value as TimeUnit)}>
                  <option value="min">Minutes</option>
                  <option value="hours">Hours</option>
                  <option value="days">Days</option>
                </select>
              </div>
            </div>
            <div className="beta-filter-date-range-display beta-filter-date-range-display--stacked">
              <span>From (Zulu): {zuluRelativeRange.from}</span>
              <span>To (Zulu): {zuluRelativeRange.to}</span>
            </div>
          </>
        ) : (
          <>
            <div className="beta-filter-field">
              <span className="beta-filter-label">From (Zulu)</span>
              <DatePicker
                selected={startDate}
                onChange={(date) => setStartDate(date)}
                showTimeSelect
                timeFormat="HH:mm"
                timeIntervals={15}
                dateFormat="MM/dd/yyyy HH:mm"
                maxDate={now}
                placeholderText="MM/DD/YYYY HH:MM"
                className="beta-filter-input"
                wrapperClassName="beta-datepicker-wrapper"
                calendarClassName={`beta-datepicker-calendar beta-datepicker-calendar--${theme}`}
                isClearable
                popperPlacement="bottom-start"
                portalId="root"
              />
              {startDate && <span className="beta-filter-zulu-time">Zulu: {formatDatePickerZuluDateTimeLabel(startDate)}</span>}
            </div>
            <div className="beta-filter-field">
              <span className="beta-filter-label">To (Zulu)</span>
              <DatePicker
                selected={endDate}
                onChange={(date) => setEndDate(date)}
                showTimeSelect
                timeFormat="HH:mm"
                timeIntervals={15}
                dateFormat="MM/dd/yyyy HH:mm"
                maxDate={now}
                minDate={startDate || undefined}
                placeholderText="MM/DD/YYYY HH:MM"
                className="beta-filter-input"
                wrapperClassName="beta-datepicker-wrapper"
                calendarClassName={`beta-datepicker-calendar beta-datepicker-calendar--${theme}`}
                isClearable
                popperPlacement="bottom-start"
                portalId="root"
              />
              {endDate && <span className="beta-filter-zulu-time">Zulu: {formatDatePickerZuluDateTimeLabel(endDate)}</span>}
            </div>
          </>
        )}
      </div>

      {/* Action buttons */}
      <div className="beta-filter-sidebar-actions">
        <button className="beta-filter-btn beta-filter-btn--primary" type="submit" disabled={isLoading}>
          {isLoading ? 'Searching…' : 'Search'}
        </button>
        <button className="beta-filter-btn beta-filter-btn--secondary" type="button" onClick={handleReset}>
          Reset
        </button>
      </div>
    </form>
  );
});

export default BetaFilterBar;
